import { useEffect, useState } from "react";
import Parse from "parse";
import { useGame } from "../header/GameContext";
import ButtonOnly from "../button/buttonOnly";
import InputText from "../inputText/inputText";
import { saveToSheet } from "../../api/sheet";

import "./login.css";

/* ========================= 유틸 ========================= */
// 입장 코드에서 prefix, 회차 추출
// "spb001" → prefix="sp", round=2 (b=2)
const getRoundFromCode = (inCode, prefix = "sp") => {
  if (!inCode || !prefix) return null;
  const alpha = inCode.slice(prefix.length, prefix.length + 1);
  const round = alpha.charCodeAt(0) - 96; // a=1, b=2 ...
  return round > 0 ? round : null;
};

// KST 오늘 날짜
const todayKST = () => {
  const kst = new Date(Date.now() + 9 * 60 * 60 * 1000);
  return kst.toISOString().slice(0, 10);
};

const PROG_PREFIX = "sp"; // ← 스파이x 접두어
const COURSE_CLASS = "CourseList"; // ← 관리자 과정 테이블
const DEFAULT_TIME = 3600; // ← CourseList 조회 실패 시 기본값(초)

function LoginName({ handleName, setHandleName, onFinish, defaultPath }) {
  const {
    playerKeyCode,
    setPlayerKeyCode,
    setPlayerName,
    pauseTimer,
    introTeamName,
    introInCodeV,
    userRole,
    syncTimer,
    setClearedStages,
  } = useGame();

  const teamName = introTeamName || localStorage.getItem("PLAYER_TEAM") || "";
  const inCode = introInCodeV || localStorage.getItem("PLAYER_INCODE") || "";
  const role = userRole || localStorage.getItem("PLAYER_ROLE") || "";
  const keyCode =
    playerKeyCode || localStorage.getItem("PLAYER_KEY_CODE") || "";

  const safeName = handleName || "";
  const isActive = safeName.trim().length > 0;
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    pauseTimer();
  }, [pauseTimer]);

  /* ========================= CourseList에서 게임시간 조회 ========================= */
  const fetchGameTime = async () => {
    try {
      const round = getRoundFromCode(inCode, PROG_PREFIX);
      const today = todayKST();

      console.log("🔍 fetchGameTime 조회:", {
        inCode,
        round,
        today,
        PROG_PREFIX,
      });

      if (round === null) {
        console.warn("⚠ 회차 추출 실패 → 기본값:", DEFAULT_TIME);
        return DEFAULT_TIME;
      }

      const q = new Parse.Query(COURSE_CLASS);
      q.equalTo("prefix", PROG_PREFIX);
      q.equalTo("round", round);
      q.equalTo("useDate", today);
      q.descending("createdAt");
      const course = await q.first();

      console.log(
        "🔍 CourseList 조회 결과:",
        course ? course.toJSON() : "없음",
      );

      if (course) {
        const durationSec = course.get("duration") * 60;
        console.log(
          `✅ 게임시간: ${course.get("duration")}분 → ${durationSec}초`,
        );
        return durationSec;
      }

      console.warn("⚠ CourseList 과정 없음 → 기본값:", DEFAULT_TIME);
      return DEFAULT_TIME;
    } catch (e) {
      console.warn("게임시간 조회 실패:", e.message);
      return DEFAULT_TIME;
    }
  };

  /* ========================= 시작 ========================= */
  const handleStart = async () => {
    if (!safeName.trim()) {
      alert("이름(닉네임) 입력해주세요.");
      return;
    }

    setIsLoading(true);

    try {
      const GameUser = Parse.Object.extend("spyx");

      // 1️⃣ 팀명 + 입장코드 + 닉네임 모두 일치 → 이어하기
      const exactQuery = new Parse.Query(GameUser);
      exactQuery.equalTo("introTeam", teamName);
      exactQuery.equalTo("introInCode", inCode);
      exactQuery.equalTo("playerName", handleName);
      const existingUser = await exactQuery.first();

      if (existingUser) {
        // CourseList에서 최신 게임시간 조회 (TOTAL_GAME_TIME 기준값)
        const latestTime = await fetchGameTime();

        // TOTAL_GAME_TIME은 항상 관리자 설정값으로 고정
        localStorage.setItem("ADMIN_TIME_KEY", String(latestTime));

        // 남은시간: 저장된 시간이 관리자 설정시간보다 작으면 복원, 아니면 최신시간
        const savedTime = existingUser.get("timeRemaining");
        const restoreTime =
          savedTime && savedTime < latestTime ? savedTime : latestTime;

        // time만 복원 (TOTAL_GAME_TIME은 건드리지 않음)
        localStorage.setItem("GAME_TIME_REMAIN", String(restoreTime));
        syncTimer(restoreTime);
        console.log(
          "✅ 이어하기 - 관리자설정:",
          latestTime,
          "/ 복원시간:",
          restoreTime,
        );

        // 단서 복원
        const savedStages = existingUser.get("clearedStages");
        if (savedStages && Array.isArray(savedStages)) {
          localStorage.setItem("clearedStages", JSON.stringify(savedStages));
          setClearedStages(new Set(savedStages));
          console.log("✅ 이어하기 - 단서 복원:", savedStages);
        }

        localStorage.setItem("gameUserId", existingUser.id);
        setPlayerName(handleName);

        // ✅ keyCode 복원 — quizeCheck에서 playerKeyCode로 유저 조회에 필요
        const savedKeyCode = existingUser.get("keyCode") || keyCode;
        if (savedKeyCode) {
          localStorage.setItem("PLAYER_KEY_CODE", savedKeyCode);
          setPlayerKeyCode(savedKeyCode);
        }

        onFinish?.({
          defaultPath: "/keywordInput",
          state: { isContinue: true },
        });
        return;
      }

      // 2️⃣ 닉네임 중복 체크
      const nickQuery = new Parse.Query(GameUser);
      nickQuery.equalTo("playerName", handleName);
      const nickExists = await nickQuery.first();

      if (nickExists) {
        alert("이미 사용 중인 닉네임입니다. 다른 닉네임을 입력해주세요.");
        setIsLoading(false);
        return;
      }

      // 3️⃣ CourseList에서 게임시간 조회
      const adminTime = await fetchGameTime();

      // 4️⃣ 신규 생성
      const newUser = new GameUser();
      newUser.set("introTeam", teamName);
      newUser.set("introInCode", inCode);
      newUser.set("keyCode", keyCode);
      newUser.set("userRole", role);
      newUser.set("playerName", handleName);
      newUser.set("clearedStages", []);
      newUser.set("timeRemaining", adminTime);
      await newUser.save();

      // ✅ GameContext 타이머에 즉시 반영
      syncTimer(adminTime);
      localStorage.setItem("GAME_TIME_REMAIN", String(adminTime));
      localStorage.setItem("ADMIN_TIME_KEY", String(adminTime));
      localStorage.setItem("gameUserId", newUser.id);
      setPlayerName(handleName);

      console.log("✅ 신규 생성 완료 - ID:", newUser.id, "시간:", adminTime);

      try {
        await saveToSheet({
          keyCode,
          playerName: handleName,
          introTeam: teamName,
          introCode: inCode,
          userRole: role,
        });
      } catch (sheetError) {
        console.error("시트 저장 실패:", sheetError.message);
      }

      onFinish?.({
        defaultPath,
        state: { DateVal: "intro1", titImgClass: "infoTit" },
      });
    } catch (e) {
      console.error(e);
      alert("저장 실패. 다시 시도해주세요.");
      setIsLoading(false);
    }
  };

  return (
    <div id="content">
      <div className="box2">
        <div id="box_con" className="main_box2">
          <div className="main_Bimg"></div>
          <div className="pdLR16">
            <InputText
              textClass="input02 mt16"
              textValue="이름을 입력하세요."
              handleName={handleName}
              setHandleName={setHandleName}
            />
            <ButtonOnly
              btnValue={
                isLoading ? <span className="btn-spinner white" /> : "게임 시작"
              }
              btnClass={`inputBtn01 mt16 ${isActive ? "active" : ""}`}
              onFinish={handleStart}
              disabled={!isActive || isLoading}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginName;
